// Refunds By Day report page — audits every refund issued at the branch
// with the original method, payout, reason, requesting staff and
// approving manager. Split out of the old SalesReportsTab so it can be
// deep-linked from the Reports directory.
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams } from "react-router-dom";
import { staffApi, formatApiError, formatBranchMoney } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";
import ReportShell from "./ReportShell";
import DateRangeFilter, { useDateRange } from "./DateRangeFilter";

function fmtTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      timeZone: "Asia/Makassar", day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function RefundsReport() {
  const { bid } = useParams();
  const [branch, setBranch] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { preset, setPreset, customFrom, setCustomFrom, customTo, setCustomTo, range } = useDateRange("last_month");

  useEffect(() => {
    staffApi.get("/branches").then((r) => setBranch(r.data.find((b) => b.id === bid) || null));
  }, [bid]);

  const load = useCallback(async () => {
    if (!bid) return;
    setLoading(true);
    try {
      const r = await staffApi.get(`/reports/branch/${bid}/refunds`, { params: range });
      setData(r.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [bid, range]);

  useEffect(() => { load(); }, [load]);

  const exportSpec = useMemo(() => {
    if (!data) return null;
    // Iter-318 · Excel emits raw negative numbers for refund amounts so
    // SUM() reads naturally as a deduction column.
    const money = (v) => formatBranchMoney(branch, v || 0);
    const signedMoney = (v) => (v < 0 ? `- ${money(Math.abs(v))}` : money(v));
    const rows = (data.refunds || []).map((r) => ({
      when: fmtTime(r.at),
      amount: -Math.abs(Number(r.amount) || 0),
      original_method: r.original_method,
      payout: r.payout_label,
      reason: r.reason,
      requester: r.requester_name,
      approver: r.approver_name,
      order: `#${r.order_number}`,
    }));
    return {
      filename: "refunds-by-day",
      range: { date_from: data.date_from, date_to: data.date_to },
      columns: [
        { header: "When", key: "when" },
        { header: "Amount", key: "amount", numeric: true, format: signedMoney, align: "right" },
        { header: "Original Method", key: "original_method" },
        { header: "Payout", key: "payout" },
        { header: "Reason", key: "reason" },
        { header: "Staff Requesting", key: "requester" },
        { header: "Manager Approving", key: "approver" },
        { header: "Order", key: "order" },
      ],
      rows,
      footerRow: ["Total", -Math.abs(Number(data.total) || 0), "", "", "", "", "", ""],
    };
  }, [data, branch]);

  return (
    <ReportShell title="Refunds By Day" subtitle="Every refund issued in the window with its audit chain — requesting staff, approving manager, original method." branchName={branch?.name} tab="sales" exportSpec={exportSpec}>
      <div className="space-y-4">
        <DateRangeFilter
          preset={preset} setPreset={setPreset}
          customFrom={customFrom} setCustomFrom={setCustomFrom}
          customTo={customTo} setCustomTo={setCustomTo}
          loading={loading} onRefresh={load}
        />

        <Card className="rounded-lg overflow-hidden" data-testid="refunds-by-day-card">
          <CardContent className="p-0">
            <div className="p-4 border-b border-border flex items-baseline justify-between">
              <div>
                <p className="tracking-overline text-[10px] text-muted-foreground">Report</p>
                <h2 className="text-lg font-semibold">Refunds By Day</h2>
              </div>
              {data && (
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{data.count} refund{data.count === 1 ? "" : "s"} · total</div>
                  <div className="text-xl font-semibold" data-testid="refunds-grand-total">- {formatBranchMoney(branch, data.total)}</div>
                </div>
              )}
            </div>
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="uppercase text-[10px] tracking-wider">When</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider text-right">Amount</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Original Method</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Payout</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Reason</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Staff Requesting</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Manager Approving</TableHead>
                  <TableHead className="uppercase text-[10px] tracking-wider">Order</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(!data || data.count === 0) ? (
                  <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8">
                    {loading ? "Loading…" : "No refunds in this range."}
                  </TableCell></TableRow>
                ) : data.refunds.map((r, i) => (
                  <TableRow key={`${r.order_id}-${i}`} data-testid={`refund-row-${i}`}>
                    <TableCell className="text-xs whitespace-nowrap">{fmtTime(r.at)}</TableCell>
                    <TableCell className="text-right font-medium">{formatBranchMoney(branch, r.amount)}</TableCell>
                    <TableCell className="text-sm">{r.original_method}</TableCell>
                    <TableCell className="text-sm">{r.payout_label}</TableCell>
                    <TableCell className="text-sm max-w-xs truncate" title={r.reason}>{r.reason}</TableCell>
                    <TableCell className="text-sm">{r.requester_name}</TableCell>
                    <TableCell className="text-sm">{r.approver_name}</TableCell>
                    <TableCell className="text-xs">
                      <Link to={`/staff/branch/${bid}/orders?order=${r.order_id}`} className="text-primary hover:underline inline-flex items-center gap-1" data-testid={`refund-order-link-${i}`}>
                        #{r.order_number} <ExternalLink className="w-3 h-3" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </ReportShell>
  );
}
